import { IBiomaterial, IEvent, IPatient } from '@zero-dash/types';
import { useSnackbar } from 'notistack';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';
import {
  BIOMARKER_LOADING_STATE,
  DISEASE_BURDEN_LOADING_STATE,
} from '@/constants/PatientDashboard/patientTimeline';
import { IAnalysisSet } from '@/types/Analysis/AnalysisSets.types';
import { IBiosample } from '@/types/Analysis/Biosamples.types';
import { IReport } from '@/types/Reports/Reports.types';
import {
  IBiomarkerFetchState,
  IDiseaseBurdenFetchState,
} from '@/types/PatientDashboard/PatientTimeline.types';
import {
  fetchBiomarkerSnvs,
  getBiomarkerBiosampleIds,
} from '@/components/PatientDashboard/TimelineTab/Timeline/Biomarker.util';
import {
  fetchWispResults,
  getDiseaseBurdenBiosampleIds,
} from '@/components/PatientDashboard/TimelineTab/Timeline/DiseaseBurden.util';

const runChartLaneFetch = <T, >(
  fetch: (signal: AbortSignal) => Promise<T>,
  onResolved: (outcome: T) => void,
): (() => void) => {
  const controller = new AbortController();

  fetch(controller.signal).then((outcome) => {
    if (controller.signal.aborted) {
      return;
    }
    onResolved(outcome);
  });

  return () => controller.abort();
};

interface IParams {
  publicSubjectId?: string;
  patientRecords: IPatient[];
  arePatientRecordsSettled: boolean;
  biomaterials: IBiomaterial[];
  hasFetchedBiomaterials: boolean;
  biomaterialsFailed: boolean;
  onRetrySamples: () => void;
}

interface IPatientTimelineData {
  events?: IEvent[];
  biosamples: IBiosample[];
  reports: IReport[];
  analysisSets: IAnalysisSet[];
  biomarker: IBiomarkerFetchState;
  diseaseBurden: IDiseaseBurdenFetchState;
  isReportsLoading: boolean;
  retryBiomarkers: () => void;
  retryDiseaseBurden: () => void;
}

export function usePatientTimelineData({
  publicSubjectId,
  patientRecords,
  arePatientRecordsSettled,
  biomaterials,
  hasFetchedBiomaterials,
  biomaterialsFailed,
  onRetrySamples,
}: IParams): IPatientTimelineData {
  const zeroDashSdk = useZeroDashSdk();
  const { enqueueSnackbar } = useSnackbar();

  const [events, setEvents] = useState<IEvent[]>();
  const [biosamples, setBiosamples] = useState<IBiosample[]>([]);
  const [reports, setReports] = useState<IReport[]>([]);
  const [reportsFetched, setReportsFetched] = useState(false);
  const [analysisSets, setAnalysisSets] = useState<IAnalysisSet[]>([]);
  const [analysisSetsFetched, setAnalysisSetsFetched] = useState(false);
  const [analysisSetsFailed, setAnalysisSetsFailed] = useState(false);
  const [analysisSetsRetry, setAnalysisSetsRetry] = useState(0);
  const [biomarker, setBiomarker] = useState<IBiomarkerFetchState>(BIOMARKER_LOADING_STATE);
  const [biomarkerRetry, setBiomarkerRetry] = useState(0);
  const [diseaseBurden, setDiseaseBurden] = useState<IDiseaseBurdenFetchState>(
    DISEASE_BURDEN_LOADING_STATE,
  );
  const [diseaseBurdenRetry, setDiseaseBurdenRetry] = useState(0);

  const [renderedSubjectId, setRenderedSubjectId] = useState(publicSubjectId);
  if (renderedSubjectId !== publicSubjectId) {
    setRenderedSubjectId(publicSubjectId);
    setEvents(undefined);
    setBiosamples([]);
    setAnalysisSets([]);
    setAnalysisSetsFetched(false);
    setReports([]);
    setReportsFetched(false);
    setAnalysisSetsFailed(false);
    setBiomarker(BIOMARKER_LOADING_STATE);
    setDiseaseBurden(DISEASE_BURDEN_LOADING_STATE);
  }

  const retryBiomarkers = useCallback((): void => {
    if (analysisSetsFailed) {
      setAnalysisSetsRetry((count) => count + 1);
    }
    if (biomaterialsFailed) {
      onRetrySamples();
    }
    setBiomarkerRetry((count) => count + 1);
  }, [analysisSetsFailed, biomaterialsFailed, onRetrySamples]);

  const retryDiseaseBurden = useCallback((): void => {
    if (analysisSetsFailed) {
      setAnalysisSetsRetry((count) => count + 1);
    }
    if (biomaterialsFailed) {
      onRetrySamples();
    }
    setDiseaseBurdenRetry((count) => count + 1);
  }, [analysisSetsFailed, biomaterialsFailed, onRetrySamples]);

  useEffect(() => {
    if (patientRecords.length) {
      return;
    }
    setEvents(arePatientRecordsSettled ? [] : undefined);
  }, [arePatientRecordsSettled, patientRecords]);

  useEffect(() => {
    let cancelled = false;
    zeroDashSdk.curation.biosamples.getBiosamples({
      publicSubjectId,
    })
      .then((patientBiosamples) => {
        if (!cancelled) {
          setBiosamples(patientBiosamples);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBiosamples([]);
          enqueueSnackbar('Could not load patient biosamples, please try again.', { variant: 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, publicSubjectId, zeroDashSdk.curation.biosamples]);

  useEffect(() => {
    if (!publicSubjectId) {
      setAnalysisSets([]);
      setAnalysisSetsFetched(true);
      return undefined;
    }
    let cancelled = false;
    setAnalysisSetsFetched(false);
    zeroDashSdk.curation.analysisSets.getAnalysisSets({ publicSubjectId })
      .then((sets) => {
        if (!cancelled) {
          setAnalysisSets(sets);
          setAnalysisSetsFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAnalysisSets([]);
          setAnalysisSetsFailed(true);
          enqueueSnackbar('Could not load patient analysis sets, please try again.', { variant: 'error' });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAnalysisSetsFetched(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [analysisSetsRetry, enqueueSnackbar, publicSubjectId, zeroDashSdk.curation.analysisSets]);

  useEffect(() => {
    const analysisSetIds = Array.from(new Set(analysisSets.map((set) => set.analysisSetId)));
    if (!analysisSetIds.length) {
      setReports([]);
      setReportsFetched(analysisSetsFetched);
      return undefined;
    }
    let cancelled = false;
    setReportsFetched(false);
    zeroDashSdk.services.reports.getReports({ analysisSetIds })
      .then((patientReports) => {
        if (!cancelled) {
          setReports(patientReports);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReports([]);
          enqueueSnackbar('Could not load patient reports, please try again.', { variant: 'error' });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReportsFetched(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [analysisSets, analysisSetsFetched, enqueueSnackbar, zeroDashSdk.services.reports]);

  const diseaseBurdenBiosampleKey = useMemo(
    () => getDiseaseBurdenBiosampleIds(analysisSets).join(),
    [analysisSets],
  );

  const diseaseBurdenBiosampleIds = useMemo(
    () => (diseaseBurdenBiosampleKey ? diseaseBurdenBiosampleKey.split(',') : []),
    [diseaseBurdenBiosampleKey],
  );

  useEffect(() => {
    if (!hasFetchedBiomaterials || !diseaseBurdenBiosampleIds.length) {
      setDiseaseBurden({
        results: [],
        isLoading: !analysisSetsFetched || !hasFetchedBiomaterials,
        hasFailure: analysisSetsFailed || biomaterialsFailed,
      });
      return undefined;
    }

    setDiseaseBurden(DISEASE_BURDEN_LOADING_STATE);

    return runChartLaneFetch(
      (signal) => fetchWispResults(
        diseaseBurdenBiosampleIds,
        (batch) => zeroDashSdk.curation.liquidBiopsy.getWispBiosamples({ biosampleIds: batch }),
        signal,
      ),
      ({ results, failedCount }) => {
        setDiseaseBurden({
          results,
          isLoading: false,
          hasFailure: failedCount > 0 || biomaterialsFailed,
        });
        if (failedCount) {
          enqueueSnackbar('Could not load some disease burden data, please try again.', { variant: 'error' });
        }
      },
    );
  }, [
    analysisSetsFailed,
    analysisSetsFetched,
    biomaterialsFailed,
    diseaseBurdenBiosampleIds,
    diseaseBurdenRetry,
    hasFetchedBiomaterials,
    enqueueSnackbar,
    zeroDashSdk.curation.liquidBiopsy,
  ]);

  const biomarkerBiosampleKey = useMemo(
    () => getBiomarkerBiosampleIds(
      analysisSets.flatMap((set) => set.biosamples ?? []),
      biomaterials,
    ).join(),
    [analysisSets, biomaterials],
  );

  const biomarkerBiosampleIds = useMemo(
    () => (biomarkerBiosampleKey ? biomarkerBiosampleKey.split(',') : []),
    [biomarkerBiosampleKey],
  );

  useEffect(() => {
    if (!biomarkerBiosampleIds.length) {
      setBiomarker({
        snvs: [],
        isLoading: !analysisSetsFetched || !hasFetchedBiomaterials,
        hasFailure: analysisSetsFailed || biomaterialsFailed,
      });
      return undefined;
    }

    setBiomarker(BIOMARKER_LOADING_STATE);

    return runChartLaneFetch(
      (signal) => fetchBiomarkerSnvs(
        biomarkerBiosampleIds,
        (id) => zeroDashSdk.snv.somatic.getReportableSampleSomaticSnvs(id, signal),
        signal,
      ),
      ({ snvs, failedCount }) => {
        setBiomarker({
          snvs,
          isLoading: false,
          hasFailure: failedCount > 0 || biomaterialsFailed,
        });
        if (failedCount) {
          enqueueSnackbar(
            `Could not load biomarker data for ${failedCount} of ${biomarkerBiosampleIds.length} samples.`,
            { variant: 'error' },
          );
        }
      },
    );
  }, [
    analysisSetsFailed,
    analysisSetsFetched,
    biomarkerBiosampleIds,
    biomarkerRetry,
    biomaterialsFailed,
    hasFetchedBiomaterials,
    enqueueSnackbar,
    zeroDashSdk.snv.somatic,
  ]);

  useEffect(() => {
    if (!patientRecords.length) {
      return undefined;
    }
    let cancelled = false;
    const patientIds = Array.from(new Set(patientRecords.map((p) => p.patientId)));
    Promise.all(patientIds.map((id) => zeroDashSdk.patient.getPatientEvents(id)))
      .then((resp) => {
        if (cancelled) {
          return;
        }
        const deduplicatedEvents = [
          ...new Map(
            resp
              .flatMap((patientResp) => patientResp?.events ?? [])
              .map((event) => [`${event.eventNumber}-${event.event}-${event.date}`, event]),
          ).values(),
        ];

        setEvents(deduplicatedEvents);
      }).catch(() => {
        if (!cancelled) {
          setEvents([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [patientRecords, zeroDashSdk.patient]);

  return {
    events,
    biosamples,
    reports,
    analysisSets,
    biomarker,
    diseaseBurden,
    isReportsLoading: !analysisSetsFetched || !reportsFetched,
    retryBiomarkers,
    retryDiseaseBurden,
  };
}
